import React, { useState, useEffect } from 'react';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar.svg';
import { useIsNarrow } from '../../hooks/useIsNarrow';

const formatDate = (dateStr) => {
    if (!dateStr) return '...';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const DJPalDateRangeModal = ({ dateRange, onApply, onClose }) => {
    const [from, setFrom] = useState(dateRange?.from || '');
    const [to, setTo] = useState(dateRange?.to || '');
    const [minimized, setMinimized] = useState(false);
    // iOS renders a valueless date input as a fully blank box (no
    // placeholder), which reads as broken — overlay an "Any date" hint on
    // narrow viewports. Desktop's native inputs show their own placeholder.
    const isNarrow = useIsNarrow();

    // Esc closes the popover (without clearing an applied range).
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const preset = (days) => {
        const toD = new Date();
        const fromD = new Date(Date.now() - days * 86400000);
        const fmt = (d) => d.toISOString().slice(0, 10);
        setFrom(fmt(fromD));
        setTo(fmt(toD));
    };

    const handleApply = () => {
        if (!from && !to) {
            onApply(null);
        } else {
            onApply({ from: from || undefined, to: to || undefined });
            setMinimized(true);
        }
    };

    const handleClear = () => {
        onApply(null);
        onClose();
    };

    if (minimized && dateRange) {
        return (
            <div className="djpal-date-range-minimized" onClick={() => setMinimized(false)}>
                <div className="date-mini-content">
                    <CalendarIcon className="date-mini-icon" />
                    <span className="date-mini-text">
                        {formatDate(dateRange.from)} - {formatDate(dateRange.to)}
                    </span>
                    <span className="date-mini-badge">Active</span>
                </div>
                <button className="date-mini-close" onClick={(e) => { e.stopPropagation(); handleClear(); }}>&times;</button>
            </div>
        );
    }

    return (
        <div className="djpal-date-range-inline">
            <div className="date-header-row">
                <span className="date-header-title">Date range</span>
                <span className="date-icon-btn" title="Close" onClick={onClose}>&times;</span>
            </div>
            <div className="date-presets-row">
                <button type="button" className="date-preset-chip" onClick={() => preset(90)}>Last 3 months</button>
                <button type="button" className="date-preset-chip" onClick={() => preset(365)}>Last year</button>
                <button type="button" className="date-preset-chip" onClick={() => preset(3 * 365)}>Last 3 years</button>
            </div>
            <div className="date-inline-row">
                <label>
                    From
                    <span className="date-input-wrap">
                        <input
                            type="date"
                            value={from}
                            onChange={(e) => setFrom(e.target.value)}
                        />
                        {isNarrow && !from && <span className="date-empty-hint">Any date</span>}
                    </span>
                </label>
                <label>
                    To
                    <span className="date-input-wrap">
                        <input
                            type="date"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                        />
                        {isNarrow && !to && <span className="date-empty-hint">Any date</span>}
                    </span>
                </label>
                <div className="date-inline-actions">
                    {dateRange && (
                        <button className="clear-btn" onClick={handleClear}>Clear</button>
                    )}
                    <button className="apply-btn" onClick={handleApply}>Apply</button>
                </div>
            </div>
        </div>
    );
};

export default DJPalDateRangeModal;
